import Contact from '@repo/utils/models/Contact';
import Note from '@repo/utils/models/Note';
import type { ModalState, ReduxAction } from '@repo/utils/types';

const initialModalState = {
  isOpen: false,
  modalType: null,
  dispatchOrigin: null,
  contactAndNoteModal: {
    contact: new Contact(),
    note: new Note(),
    canEditContact: true,
    focusMode: false,
    creatingNewReminder: false,
  },
  newNoteDraft: null,
  settingsNav: null,
  activityItemID: null,
  groupID: null,
};

export function modalReducer(
  state: ModalState = initialModalState,
  { type, payload }: ReduxAction,
) {
  let contactInModal = state.contactAndNoteModal.contact;
  let noteInModal = state.contactAndNoteModal.note;

  switch (type) {
    case 'LOAD_LOCAL_STORE_SUCCEEDED':
      const storedNewNoteDraft = payload.get('new_note_draft');
      if (storedNewNoteDraft) {
        return {
          ...state,
          newNoteDraft: new Note(storedNewNoteDraft),
        };
      }
      return state;

    case 'OPEN_MODAL':
      return {
        ...state,
        isOpen: true,
        modalType: payload.type,
        dispatchOrigin: payload.source,
        groupID: payload.groupID,
      };

    case 'CLOSE_MODAL':
      return {
        ...state,
        isOpen: false,
        modalType: null,
        dispatchOrigin: null,
        // settingsNav: null,
        activityItemID: null,
        groupID: null,
        newNoteDraft: state.newNoteDraft ? state.newNoteDraft.removeReminder() : state.newNoteDraft,
      };

    case 'CLOSE_MODAL_AND_CLEAR_NOTE_DRAFT':
      return {
        ...state,
        isOpen: false,
        modalType: null,
        dispatchOrigin: null,
        newNoteDraft: null,
      };

    case 'START_NEW_CONTACT_IN_MODAL':
      return {
        ...state,
        isOpen: true,
        modalType: 'contactAndNote',
        contactAndNoteModal: {
          contact: payload.contact || new Contact(),
          note: state.newNoteDraft || new Note(),
          canEditContact: true,
          focusMode: false,
          creatingNewReminder: false,
        },
      };

    case 'START_NEW_NOTE_IN_MODAL':
      return {
        ...state,
        isOpen: true,
        modalType: 'contactAndNote',
        contactAndNoteModal: {
          contact: payload.contact || new Contact(),
          note: state.newNoteDraft || new Note(),
          canEditContact: true,
          focusMode: false,
          creatingNewReminder: payload.creatingNewReminder || false,
        },
      };

    case 'OPEN_NOTE_IN_MODAL':
      return {
        ...state,
        isOpen: true,
        modalType: 'contactAndNote',
        contactAndNoteModal: {
          contact: payload.contact,
          note: payload.note,
          canEditContact: false,
          focusMode: payload.focusMode || false,
          creatingNewReminder: false,
        },
      };

    case 'UPDATE_CONTACT_AND_NOTE_MODAL':
      contactInModal = payload.contact ? new Contact(payload.contact) : contactInModal;
      noteInModal = payload.note
        ? state.contactAndNoteModal.note.copyWith({ ...payload.note })
        : noteInModal;

      return {
        ...state,
        contactAndNoteModal: {
          ...state.contactAndNoteModal,
          contact: contactInModal,
          note: noteInModal,
        },
        newNoteDraft: noteInModal.isPersisted ? state.newNoteDraft : noteInModal,
      };

    case 'CREATE_CONTACT_SUCCEEDED':
      return {
        ...state,
        contactAndNoteModal: {
          ...state.contactAndNoteModal,
          contact: new Contact({ ...payload.response }),
        },
      };

    case 'CREATE_NOTE_SUCCEEDED':
      return {
        ...state,
        newNoteDraft: null,
      };

    case 'CREATE_NOTE_FAILED':
      return {
        ...state,
        newNoteDraft: new Note({ ...payload.originalNote }),
      };

    case 'OPEN_ACTIVITY_ITEM_IN_MODAL':
      return {
        ...state,
        isOpen: true,
        modalType: 'expandedActivityItem',
        activityItemID: payload.activityItemID,
      };

    case 'NAVIGATE_TO_SETTINGS':
      return {
        ...state,
        isOpen: state.isOpen && state.modalType === 'contactAndNote' ? true : false,
        settingsNav: payload?.settingsNav || { panel: 'general' },
      };

    case 'CLOSE_SETTINGS':
      return {
        ...state,
        settingsNav: null,
      };

    default:
      return state;
  }
}
